import { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";

function AnimatedCounter({ end, suffix = "", prefix = "", duration = 2 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = end / (duration * 60);
    const timer = setInterval(() => {
      start += step;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 1000 / 60);
    return () => clearInterval(timer);
  }, [inView, end, duration]);

  return (
    <span ref={ref} className="tabular-nums" style={{ fontVariantNumeric: "tabular-nums" }}>
      {prefix}{count}{suffix}
    </span>
  );
}

export default function ImpactStats() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  const stats = [
    { value: 40, prefix: "+", suffix: "%", label: "Aumento de revenue", sublabel: "de media en nuestros pilotos", color: "#00E5FF" },
    { value: 60, prefix: "-", suffix: "%", label: "Reducción de costes", sublabel: "operativos automatizados", color: "#F5A623" },
    { value: 72, prefix: "", suffix: "h", label: "Solución a medida", sublabel: "personalizada para tu negocio", color: "#7B5EA7" },
    { value: 80, prefix: "", suffix: "%", label: "Sectores cubiertos", sublabel: "hostelería, retail, servicios, industria...", color: "#6B7FD7" },
  ];

  return (
    <section data-testid="impact-stats-section" ref={ref} className="relative py-28 md:py-40 px-6 md:px-12 overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-40" />

      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.9 }}
        className="text-center mb-16 md:mb-24 max-w-3xl mx-auto"
      >
        <p className="text-[#00E5FF] text-xs font-bold tracking-[0.35em] uppercase mb-5">Impacto real</p>
        <h2 data-testid="impact-heading" className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-tight text-center">
          Números que hablan{" "}
          <span className="bg-gradient-to-r from-[#00E5FF] to-[#7B5EA7] bg-clip-text text-transparent">por sí solos.</span>
        </h2>
      </motion.div>

      <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-8">
        {stats.map((stat, i) => (
          <motion.div
            key={i}
            data-testid={`impact-stat-${i}`}
            initial={{ opacity: 0, y: 70, rotateX: 20 }}
            animate={isInView ? { opacity: 1, y: 0, rotateX: 0 } : {}}
            transition={{ duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
            className="relative text-center p-6 md:p-8 rounded-2xl glass-card-strong group hover:scale-[1.03] transition-transform duration-500"
          >
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-10 h-0.5 rounded-full group-hover:w-16 transition-all duration-500 opacity-70" style={{ background: stat.color }} />
            <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-20 h-20 rounded-full opacity-0 group-hover:opacity-30 transition-opacity duration-700" style={{ background: `radial-gradient(circle, ${stat.color}, transparent)`, filter: "blur(20px)" }} />

            <div className="text-4xl sm:text-5xl md:text-6xl font-black mb-3 stat-glow" style={{ color: stat.color }}>
              <AnimatedCounter end={stat.value} suffix={stat.suffix} prefix={stat.prefix} duration={1.5} />
            </div>
            <p className="text-white font-semibold text-sm md:text-base mb-1 text-center">{stat.label}</p>
            <p className="text-white/35 text-xs text-center">{stat.sublabel}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
